import { addDays, isValid, parseISO } from 'date-fns'

// Trailing ISO-8601 offset: `Z`, or `±HH`, `±HHMM`, `±HH:MM`.
const OFFSET_RE = /(Z|[+-]\d{2}:?\d{0,2})$/
const HOUR_RE = /T(\d{2}):/

/**
 * Opal stores most dates in the workspace timezone (typically Pacific). A JS
 * `Date` renders in the viewer's local zone, so a value stored at midnight can
 * land on the previous calendar day for viewers further behind UTC than the
 * stored offset. When that happens we add a day so the rendered calendar date
 * matches the stored one.
 */
export const dateAdjustedForTimezone = (ISODateString: string) => {
  // argument sanity check for JavaScript callers
  if (typeof ISODateString !== 'string') return null

  const date = parseISO(ISODateString)
  // don't continue if input isn't ISO-8601
  if (!isValid(date)) return null

  // bail on a plain `yyyy-mm-dd` with no time component
  const hourMatch = ISODateString.match(HOUR_RE)
  if (!hourMatch) return null

  const storedHour = parseInt(hourMatch[1], 10)

  // Stored UTC offset expressed as hours-behind-UTC to match the sign of
  // `getTimezoneOffset()` (Pacific `-07:00` → 7, Auckland `+12:00` → -12).
  // Parsing the sign and hour explicitly avoids the previous bug where
  // splitting the whole string on `-` mis-read ahead-of-UTC offsets (the
  // day-of-month was parsed as the offset). Without a parseable offset we
  // can't compare, so leave the date unadjusted.
  const offsetMatch = ISODateString.match(OFFSET_RE)
  if (!offsetMatch) return date

  const offset = offsetMatch[1]
  const storedOffsetHoursBehindUtc =
    offset === 'Z'
      ? 0
      : -((offset[0] === '-' ? -1 : 1) * parseInt(offset.slice(1, 3), 10))

  // always based on the viewer's computer timezone, in minutes
  const viewerHoursBehindUtc = date.getTimezoneOffset() / 60

  if (storedHour === 0 && viewerHoursBehindUtc > storedOffsetHoursBehindUtc) {
    return addDays(date, 1)
  }

  return date
}
